import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronDown, Download, Globe2, Loader2, PackageCheck, Plus, Search, Trash2, Plane } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import ShareTrackingActions from "../../components/ShareTrackingActions";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";
const STEPS = ["Sender", "Destination", "Boxes & Items", "Couriers", "Payment", "Labels"];

const emptyItem = { name: "", sku: "", quantity: "1", unit_price: "", currency: "INR", hs_code: "", country_of_origin: "IN", item_weight: "", category: "", description: "" };
const emptyBox = () => ({ weight: "", length: "", width: "", height: "", items: [{ ...emptyItem }] });

function Flag({ code, className = "h-4 w-6" }) {
  return <img src={`https://flagcdn.com/w40/${code}.png`} alt={code} className={`${className} rounded-sm object-cover`} loading="lazy" />;
}

function CountrySelect({ countries, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = countries.find((c) => c.code === value);
  const list = countries.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative sm:col-span-2" data-testid="intl-country-select">
      <button type="button" onClick={() => setOpen(!open)} className={`${inputCls} flex items-center justify-between text-left`}>
        {sel ? <span className="flex items-center gap-2"><Flag code={sel.flag} /> {sel.name} <span className="mono text-xs text-slate-400">{sel.code} · {sel.currency}</span></span>
          : <span className="text-slate-400">Select destination country *</span>}
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input autoFocus data-testid="intl-country-search" className="w-full py-1 text-sm focus:outline-none" placeholder="Search country…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {list.map((c) => (
              <button key={c.code} type="button" data-testid={`country-option-${c.code}`}
                onClick={() => { onChange(c.code); setOpen(false); setQ(""); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-shipany-blue/5">
                <Flag code={c.flag} /> <span className="font-medium">{c.name}</span>
                <span className="mono ml-auto text-xs text-slate-400">{c.code}</span>
              </button>
            ))}
            {!list.length && <p className="px-3 py-4 text-center text-xs text-slate-400">No matching country</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function International() {
  const [view, setView] = useState("list");
  const [shipments, setShipments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [step, setStep] = useState(0);
  const [warehouseId, setWarehouseId] = useState("");
  const [dest, setDest] = useState({ country: "", name: "", company: "", address1: "", address2: "", city: "", state: "", postal_code: "", phone: "", email: "", tax_id: "" });
  const [boxes, setBoxes] = useState([emptyBox()]);
  const [purpose, setPurpose] = useState("Sale");
  const [rates, setRates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payMode, setPayMode] = useState("wallet");

  const loadShipments = () => api.get("/intl/shipments").then(({ data }) => setShipments(data)).catch(() => {});
  useEffect(() => {
    loadShipments();
    api.get("/intl/countries").then(({ data }) => setCountries(data)).catch(() => {});
    api.get("/warehouses").then(({ data }) => {
      setWarehouses(data);
      const def = data.find((w) => w.is_default) || data[0];
      if (def) setWarehouseId(def.warehouse_id);
    }).catch(() => {});
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, []);

  const country = useMemo(() => countries.find((c) => c.code === dest.country), [countries, dest.country]);
  const warehouse = useMemo(() => warehouses.find((w) => w.warehouse_id === warehouseId), [warehouses, warehouseId]);

  const totals = useMemo(() => {
    let actual = 0, vol = 0, billed = 0, value = 0;
    for (const b of boxes) {
      const w = parseFloat(b.weight) || 0;
      const v = ((parseFloat(b.length) || 0) * (parseFloat(b.width) || 0) * (parseFloat(b.height) || 0)) / 5000;
      const raw = Math.round(Math.max(w, v) * 1000) / 1000;
      actual += w; vol += v; billed += Math.ceil(raw);
      for (const it of b.items) value += (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
    }
    return { actual, vol, billed, value };
  }, [boxes]);

  const setBox = (i, patch) => setBoxes(boxes.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const setItem = (bi, ii, patch) => setBox(bi, { items: boxes[bi].items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) });

  const next = () => {
    if (step === 0 && !warehouseId) return toast.error("Select a sender warehouse");
    if (step === 1) {
      if (!dest.country || !dest.name || !dest.address1 || !dest.city || !dest.phone) return toast.error("Fill all required destination fields");
      if (country?.state_required && !dest.state) return toast.error(`State/Province is required for ${country.name}`);
      if (country?.postal_required && !dest.postal_code) return toast.error(`Postal/ZIP code is required for ${country.name}`);
    }
    if (step === 2) {
      for (const [i, b] of boxes.entries()) {
        if (!(parseFloat(b.weight) > 0) || !(parseFloat(b.length) > 0) || !(parseFloat(b.width) > 0) || !(parseFloat(b.height) > 0))
          return toast.error(`Box ${i + 1}: enter weight and all dimensions`);
        if (!b.items.length || b.items.some((it) => !it.name || !(parseFloat(it.unit_price) > 0) || !(parseInt(it.quantity) > 0)))
          return toast.error(`Box ${i + 1}: every item needs a name, quantity and unit price`);
      }
      if (!(totals.value > 0)) return toast.error("Total shipment value must be above ₹0");
    }
    setStep(step + 1);
  };

  const fetchRates = async () => {
    setLoadingRates(true); setRates(null); setSelected(null);
    try {
      const { data } = await api.post("/intl/rates", {
        destination_country: dest.country,
        destination_postal_code: dest.postal_code || "",
        destination_state: dest.state || "",
        boxes: boxes.map((b) => ({
          weight: parseFloat(b.weight), length: parseFloat(b.length), width: parseFloat(b.width), height: parseFloat(b.height),
          items: b.items.map((it) => ({ ...it, quantity: parseInt(it.quantity) || 1, unit_price: parseFloat(it.unit_price) || 0, item_weight: parseFloat(it.item_weight) || 0 })),
        })),
      });
      setRates(data);
      const rec = data.quotes.find((q) => q.recommended) || data.quotes[0];
      if (rec) setSelected(`${rec.courier_name}||${rec.service}`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoadingRates(false); }
  };

  useEffect(() => {
    if (step === 3) fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const book = async () => {
    setBooking(true);
    try {
      const [courier_name, service] = (selected || "").split("||");
      const { data } = await api.post("/intl/shipments", {
        warehouse_id: warehouseId,
        destination: dest,
        purpose,
        payment_mode: payMode,
        courier_name, service,
        boxes: boxes.map((b) => ({
          weight: parseFloat(b.weight), length: parseFloat(b.length), width: parseFloat(b.width), height: parseFloat(b.height),
          items: b.items.map((it) => ({ ...it, quantity: parseInt(it.quantity) || 1, unit_price: parseFloat(it.unit_price) || 0, item_weight: parseFloat(it.item_weight) || 0 })),
        })),
      });
      setBooked(data);
      setStep(5);
      loadShipments();
      toast.success(`International shipment ${data.shipment_number} booked!`);
      if (data.forwarding_status === "failed") toast.warning("International booking done — domestic forwarding will be retried by our team.");
    } catch (e) {
      toast.error(fmtErr(e), e.response?.status === 402 ? { action: { label: "Add Funds", onClick: () => (window.location.href = "/app/wallet") } } : undefined);
    } finally { setBooking(false); }
  };

  const selQuote = rates?.quotes.find((q) => `${q.courier_name}||${q.service}` === selected);

  if (view === "list") return (
    <div data-testid="international-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><Globe2 className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">International Shipping</h1>
            <p className="text-sm text-slate-500">Powered by IndiaFast International — one booking, automatic forwarding, two labels.</p>
          </div>
        </div>
        <button onClick={() => setView("create")} data-testid="new-intl-shipment-btn"
          className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> New International Shipment
        </button>
      </div>

      <div className="mt-6 space-y-3" data-testid="intl-shipments-list">
        {shipments.map((s) => (
          <div key={s.shipment_id} data-testid={`intl-card-${s.shipment_number}`}
            className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-shipany-blue/40 hover:shadow-lg hover:shadow-slate-200/60">
            <Link to={`/app/international/${s.shipment_id}`} className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-lg">
                <Flag code="in" /> <Plane className="h-4 w-4 text-shipany-blue" /> <Flag code={(s.destination_country || "").toLowerCase()} />
              </div>
              <div className="min-w-52 flex-1">
                <p className="mono font-bold text-shipany-navy">{s.shipment_number}</p>
                <p className="text-xs text-slate-400">India → {s.destination_country_name} · {s.courier_name} {s.service}</p>
              </div>
              <div className="text-xs text-slate-500"><span className="font-semibold text-shipany-navy">{s.total_billed_weight} kg</span> billed · {s.total_boxes} box{s.total_boxes > 1 ? "es" : ""}</div>
              <div className="mono font-bold text-shipany-navy">{inr(s.customer_amount)}</div>
              <span className="rounded-full bg-shipany-blue/10 px-3 py-1 text-[11px] font-bold text-shipany-blue">{s.stage_label}</span>
              {s.payment_mode === "cash" && s.payment_status !== "settled" && (
                <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-700" data-testid={`intl-cash-badge-${s.shipment_number}`}>Cash · final bill pending</span>
              )}
            </Link>
            <ShareTrackingActions code={s.international_awb || s.shipment_number} label="international shipment" testId={`share-intl-${s.shipment_number}`} compact />
          </div>
        ))}
        {!shipments.length && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <Globe2 className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No international shipments yet. Create your first one — we handle the domestic forwarding automatically.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl" data-testid="intl-create-page">
      <button onClick={() => { setView("list"); setStep(0); setBooked(null); }} className="mb-4 flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-shipany-blue" data-testid="intl-back-to-list">
        <ArrowLeft className="h-4 w-4" /> All international shipments
      </button>
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">New International Shipment</h1>
      <div className="mt-6 flex items-center gap-0" data-testid="intl-wizard-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-shipany-blue text-white glow-blue" : "bg-slate-200 text-slate-500"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`ml-1.5 hidden text-[11px] font-semibold lg:block ${i === step ? "text-shipany-navy" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className={`mx-1.5 h-0.5 flex-1 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        {step === 0 && (
          <div data-testid="intl-step-sender">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Sender warehouse</h2>
            <p className="mt-1 text-xs text-slate-400">Your warehouse address is the sender — no need to re-enter it.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {warehouses.map((w) => (
                <button key={w.warehouse_id} data-testid={`intl-warehouse-${w.warehouse_id}`} onClick={() => setWarehouseId(w.warehouse_id)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${warehouseId === w.warehouse_id ? "border-shipany-blue bg-shipany-blue/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="font-semibold text-shipany-navy">{w.name} {w.is_default && <span className="ml-1 rounded bg-shipany-cyan/15 px-1.5 py-0.5 text-[10px] font-bold text-shipany-blue">DEFAULT</span>}</p>
                  <p className="mt-1 text-xs text-slate-500">{w.address}, {w.city}, {w.state} — <span className="mono">{w.pincode}</span></p>
                  {w.phone && <p className="text-xs text-slate-400">Ph: {w.phone}</p>}
                </button>
              ))}
            </div>
            {!warehouses.length && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">No warehouses yet. <Link to="/app/warehouses" className="font-bold underline">Create one first</Link>.</p>}
          </div>
        )}

        {step === 1 && (
          <div data-testid="intl-step-destination">
            <h2 className="font-display text-lg font-bold text-shipany-navy">International destination</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <CountrySelect countries={countries} value={dest.country} onChange={(v) => setDest({ ...dest, country: v })} />
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">Recipient full name *</label>
                <input data-testid="intl-dest-name" className={`${inputCls} mt-0.5`} placeholder="e.g. John Smith" value={dest.name} onChange={(e) => setDest({ ...dest, name: e.target.value })} /></div>
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">Company (optional)</label>
                <input data-testid="intl-dest-company" className={`${inputCls} mt-0.5`} placeholder="e.g. Smith LLC" value={dest.company} onChange={(e) => setDest({ ...dest, company: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[10px] font-semibold uppercase text-slate-400">Address line 1 *</label>
                <input data-testid="intl-dest-address1" className={`${inputCls} mt-0.5`} placeholder="House/plot, street" value={dest.address1} onChange={(e) => setDest({ ...dest, address1: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[10px] font-semibold uppercase text-slate-400">Address line 2 (optional)</label>
                <input data-testid="intl-dest-address2" className={`${inputCls} mt-0.5`} placeholder="Area, landmark" value={dest.address2} onChange={(e) => setDest({ ...dest, address2: e.target.value })} /></div>
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">City *</label>
                <input data-testid="intl-dest-city" className={`${inputCls} mt-0.5`} placeholder="e.g. New York" value={dest.city} onChange={(e) => setDest({ ...dest, city: e.target.value })} /></div>
              {country && country.state_required !== false && (
                <div><label className="text-[10px] font-semibold uppercase text-slate-400">{`State / Province${country.state_required ? " *" : " (optional)"}`}</label>
                  <input data-testid="intl-dest-state" className={`${inputCls} mt-0.5`} placeholder="e.g. NY" value={dest.state} onChange={(e) => setDest({ ...dest, state: e.target.value })} /></div>
              )}
              {country && country.postal_required !== false && (
                <div><label className="text-[10px] font-semibold uppercase text-slate-400">{`ZIP / Postal code${country.postal_required ? " *" : " (optional)"}`}</label>
                  <input data-testid="intl-dest-postal" className={`${inputCls} mono mt-0.5`} placeholder="e.g. 10001" value={dest.postal_code} onChange={(e) => setDest({ ...dest, postal_code: e.target.value })} /></div>
              )}
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">Phone with country code *</label>
                <input data-testid="intl-dest-phone" className={`${inputCls} mono mt-0.5`} placeholder="e.g. +12125551234" value={dest.phone} onChange={(e) => setDest({ ...dest, phone: e.target.value })} /></div>
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">Email (optional)</label>
                <input data-testid="intl-dest-email" className={`${inputCls} mt-0.5`} placeholder="e.g. john@example.com" value={dest.email} onChange={(e) => setDest({ ...dest, email: e.target.value })} /></div>
              <div><label className="text-[10px] font-semibold uppercase text-slate-400">Tax / VAT / Import ID (optional)</label>
                <input data-testid="intl-dest-tax" className={`${inputCls} mt-0.5`} placeholder="e.g. EORI / VAT number" value={dest.tax_id} onChange={(e) => setDest({ ...dest, tax_id: e.target.value })} /></div>
            </div>
            <div className="mt-4 max-w-xs">
              <label className="text-xs font-semibold text-slate-500">Purpose of export</label>
              <select data-testid="intl-purpose" className={`${inputCls} mt-1`} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                {["Sale", "Gift", "Sample", "Return", "Personal Effects"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="intl-step-boxes">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Packages & items</h2>
              <button onClick={() => setBoxes([...boxes, emptyBox()])} data-testid="intl-add-box-btn"
                className="flex items-center gap-1.5 rounded-xl border border-shipany-blue px-4 py-2 text-xs font-bold text-shipany-blue hover:bg-shipany-blue/5">
                <Plus className="h-3.5 w-3.5" /> Add Another Box
              </button>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-2">
                {boxes.map((b, bi) => (
                  <div key={bi} className="rounded-xl border border-slate-200 p-4" data-testid={`intl-box-${bi + 1}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-display text-sm font-bold text-shipany-navy">Box {bi + 1}</p>
                      {boxes.length > 1 && (
                        <button onClick={() => setBoxes(boxes.filter((_, j) => j !== bi))} data-testid={`intl-remove-box-${bi + 1}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {[["weight", "Weight (kg) *"], ["length", "L (cm) *"], ["width", "W (cm) *"], ["height", "H (cm) *"]].map(([k, l]) => (
                        <div key={k}><label className="text-[10px] font-semibold uppercase text-slate-400">{l}</label>
                          <input data-testid={`box-${bi + 1}-${k}`} className={`${inputCls} mono mt-0.5`} value={b[k]} onChange={(e) => setBox(bi, { [k]: e.target.value })} /></div>
                      ))}
                    </div>
                    <p className="mono mt-2 text-[11px] text-slate-500" data-testid={`box-${bi + 1}-billed-line`}>
                      actual {(parseFloat(b.weight) || 0).toFixed(2)} kg · volumetric {(((parseFloat(b.length) || 0) * (parseFloat(b.width) || 0) * (parseFloat(b.height) || 0)) / 5000).toFixed(2)} kg → billed <span className="font-bold text-emerald-700">{Math.ceil(Math.round(Math.max(parseFloat(b.weight) || 0, ((parseFloat(b.length) || 0) * (parseFloat(b.width) || 0) * (parseFloat(b.height) || 0)) / 5000) * 1000) / 1000)} kg</span>
                    </p>
                    <div className="mt-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Items in this box</p>
                      {b.items.map((it, ii) => (
                        <div key={ii} className="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2.5 sm:grid-cols-6" data-testid={`box-${bi + 1}-item-${ii + 1}`}>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">Item name *</label>
                            <input className={`${inputCls} mt-0.5`} placeholder="e.g. Cotton T-Shirt" value={it.name} onChange={(e) => setItem(bi, ii, { name: e.target.value })} data-testid={`box-${bi + 1}-item-${ii + 1}-name`} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">Qty *</label>
                            <input className={`${inputCls} mono mt-0.5`} placeholder="1" value={it.quantity} onChange={(e) => setItem(bi, ii, { quantity: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">Unit price ₹ *</label>
                            <input className={`${inputCls} mono mt-0.5`} placeholder="499" value={it.unit_price} onChange={(e) => setItem(bi, ii, { unit_price: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">HS code</label>
                            <input className={`${inputCls} mono mt-0.5`} placeholder="6109" value={it.hs_code} onChange={(e) => setItem(bi, ii, { hs_code: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">Category</label>
                            <input className={`${inputCls} mt-0.5`} placeholder="Apparel" value={it.category} onChange={(e) => setItem(bi, ii, { category: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold uppercase text-slate-400">Item wt (kg)</label>
                            <div className="mt-0.5 flex items-center gap-2">
                              <input className={`${inputCls} mono`} placeholder="0.5" value={it.item_weight} onChange={(e) => setItem(bi, ii, { item_weight: e.target.value })} />
                              {b.items.length > 1 && <button onClick={() => setBox(bi, { items: b.items.filter((_, j) => j !== ii) })} className="shrink-0 text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => setBox(bi, { items: [...b.items, { ...emptyItem }] })} data-testid={`box-${bi + 1}-add-item`}
                        className="mt-2 flex items-center gap-1 text-xs font-bold text-shipany-blue hover:underline"><Plus className="h-3 w-3" /> Add item</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-3" data-testid="intl-weight-summary">
                {[["Actual Weight", totals.actual], ["Volumetric Weight (÷5000)", totals.vol], ["Billed Weight", totals.billed]].map(([l, v], i) => (
                  <div key={l} className={`rounded-xl p-4 text-center ${i === 2 ? "bg-emerald-50 ring-1 ring-emerald-200" : "bg-slate-50"}`}>
                    <p className={`text-[10px] uppercase tracking-widest ${i === 2 ? "text-emerald-600" : "text-slate-400"}`}>{l}</p>
                    <p className={`mono text-xl font-bold ${i === 2 ? "text-emerald-700" : "text-shipany-navy"}`} data-testid={`intl-${["actual", "vol", "billed"][i]}-weight`}>{v.toFixed(2)} kg</p>
                  </div>
                ))}
                <div className="rounded-xl bg-shipany-navy p-4 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">Total Shipment Value</p>
                  <p className="mono text-xl font-bold text-shipany-cyan" data-testid="intl-total-value">{inr(totals.value)}</p>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-400">Billed weight = per-box max(actual, volumetric), rounded up to the next 1 kg slab; all boxes are then summed. Volumetric divisor 5000.</p>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="intl-step-couriers">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">International couriers</h2>
              {rates && <p className="text-xs font-medium text-slate-400">Live IndiaFast rates · Billed {rates.totals.billed.toFixed(2)} kg · {rates.country.name}</p>}
            </div>
            {loadingRates && <div className="flex items-center justify-center gap-2 py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Fetching live international rates…</div>}
            {rates && !rates.quotes.length && <p className="py-10 text-center text-sm text-slate-400">No international services available for this destination right now.</p>}
            <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="intl-rates-list">
              {rates?.quotes.map((q) => {
                const key = `${q.courier_name}||${q.service}`;
                return (
                  <button key={key} data-testid={`intl-rate-${q.courier_name}-${q.service}`} onClick={() => setSelected(key)}
                    className={`rounded-xl border-2 p-5 text-left transition-all ${selected === key ? "border-shipany-blue bg-shipany-blue/5 glow-blue" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-display text-lg font-bold text-shipany-navy">{q.courier_name}</p>
                      {q.recommended && <span className="rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold uppercase text-shipany-blue">Lowest</span>}
                    </div>
                    <p className="text-xs uppercase tracking-wide text-slate-400">{q.service}</p>
                    <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{inr(q.customer_price)}</p>
                    <p className="mt-1 text-xs text-slate-500">{q.transit_days} · Billed {q.billed_weight} kg · Tracking included</p>
                    <p className="text-[10px] text-slate-400">All-inclusive: freight, taxes, fuel surcharge. Duties at destination may apply.</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && selQuote && (
          <div data-testid="intl-step-payment">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Confirm & pay</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-5">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Route</p>
                <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-shipany-navy">
                  <Flag code="in" /> {warehouse?.city}, India <ArrowRight className="h-3.5 w-3.5 text-slate-400" /> <Flag code={country?.flag} /> {dest.city}, {country?.name}
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">Service</p>
                <p className="text-sm font-semibold text-shipany-navy">{selQuote.courier_name} · {selQuote.service} · {selQuote.transit_days}</p>
                <p className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">Packages</p>
                <p className="text-sm font-semibold text-shipany-navy">{boxes.length} box{boxes.length > 1 ? "es" : ""} · Billed {totals.billed.toFixed(2)} kg · Value {inr(totals.value)}</p>
              </div>
              <div className="rounded-xl border border-shipany-blue/20 bg-shipany-blue/5 p-5">
                <div className="flex justify-between text-sm"><span className="text-slate-500">International shipping ({selQuote.courier_name})</span><span className="mono font-bold">{inr(selQuote.customer_price)}</span></div>
                <div className="mt-2 flex justify-between text-sm"><span className="text-slate-500">Domestic forwarding to IndiaFast center</span><span className="mono font-bold text-emerald-600" data-testid="forwarding-charge-zero">₹0</span></div>
                <div className="mt-3 flex justify-between border-t border-shipany-blue/20 pt-3 text-base"><span className="font-bold text-shipany-navy">{payMode === "cash" ? "Estimated total" : "Total payable"}</span><span className="mono font-extrabold text-shipany-blue" data-testid="intl-total-payable">{inr(selQuote.customer_price)}</span></div>
                <p className="mt-2 text-[11px] text-slate-400">{payMode === "cash" ? "₹0 charged now — the final bill is settled from your wallet after warehouse verification." : `Wallet balance: ${inr(wallet?.balance)} ·`} Domestic forwarding is handled by us at no cost to you.</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="intl-payment-modes">
              {[
                ["wallet", "Wallet — Pay Now", `Charged instantly from your wallet · balance ${inr(wallet?.balance)}`],
                ["cash", "Cash — Pay After Final Bill", "₹0 deducted now. We verify the actual weight at the IndiaFast warehouse, then settle the final bill from your wallet (balance may go due)."],
              ].map(([k, label, sub]) => (
                <button key={k} type="button" onClick={() => setPayMode(k)} data-testid={`intl-pay-${k}`}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${payMode === k ? "border-shipany-blue bg-shipany-blue/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <p className="text-sm font-bold text-shipany-navy">{label}</p>
                  <p className="mt-1 text-xs text-slate-500">{sub}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && booked && (
          <div className="text-center" data-testid="intl-booking-success">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"><PackageCheck className="h-8 w-8 text-emerald-600" /></span>
            <h2 className="font-display mt-4 text-2xl font-bold text-shipany-navy">International shipment booked!</h2>
            <p className="mt-1 text-sm text-slate-500" data-testid="intl-booked-payment-note">
              {booked.payment_mode === "cash"
                ? `Nothing charged yet — estimated ${inr(booked.customer_amount)}. The final bill will be settled from your wallet after warehouse verification.`
                : `Charged ${inr(booked.customer_amount)} — domestic forwarding is on us.`}
            </p>
            <div className="mx-auto mt-5 grid max-w-lg gap-3 sm:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Shipment No.</p>
                <p className="mono mt-1 text-sm font-bold text-shipany-blue" data-testid="intl-booked-number">{booked.shipment_number}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">International AWB</p>
                <p className="mono mt-1 text-sm font-bold text-shipany-blue" data-testid="intl-booked-awb">{booked.international_awb}</p>
              </div>
            </div>
            {booked.forwarding_status === "failed" && (
              <p className="mx-auto mt-3 max-w-md rounded-xl bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800" data-testid="forwarding-failed-note">
                Domestic forwarding booking failed — our team has been notified and will retry. You were NOT charged for it.
              </p>
            )}
            <p className="mt-4 text-xs font-semibold text-slate-500">Print and paste BOTH labels on your package:</p>
            <div className="mt-3 flex flex-wrap justify-center gap-3">
              <a href={`${API_BASE}/api/intl/shipments/${booked.shipment_id}/label?type=domestic`} target="_blank" rel="noreferrer" data-testid="label-domestic-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3]"><Download className="h-4 w-4" /> Domestic Forwarding Label</a>
              <a href={`${API_BASE}/api/intl/shipments/${booked.shipment_id}/label?type=international`} target="_blank" rel="noreferrer" data-testid="label-intl-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-navy px-5 py-2.5 text-sm font-bold text-white"><Download className="h-4 w-4" /> IndiaFast International Label</a>
              <a href={`${API_BASE}/api/intl/shipments/${booked.shipment_id}/label?type=both`} target="_blank" rel="noreferrer" data-testid="label-both-btn"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Print Both Labels</a>
              <Link to={`/app/international/${booked.shipment_id}`} data-testid="intl-track-btn"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Track Shipment</Link>
            </div>
          </div>
        )}

        {step < 5 && (
          <div className="mt-8 flex justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="intl-wizard-back-btn"
              className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 3 && (
              <button onClick={next} data-testid="intl-wizard-next-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] glow-blue">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 3 && (
              <button onClick={() => selected ? setStep(4) : toast.error("Select a courier service")} data-testid="intl-select-courier-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] glow-blue">
                Review & Pay <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 4 && (
              <button onClick={book} disabled={booking} data-testid="intl-book-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-cyan px-6 py-2.5 text-sm font-bold text-shipany-navy transition-colors hover:bg-[#0EB4DE] disabled:opacity-50 glow-cyan">
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {payMode === "cash" ? "Book Now — Pay After Final Bill" : `Pay ${selQuote ? inr(selQuote.customer_price) : ""} & Book`}
              </button>
            )}
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200" data-testid="packing-guidelines">
          <img src="/packing-instructions.webp" alt="Box packing instructions" className="w-full" loading="lazy" data-testid="packing-instructions-image" />
        </div>
      </div>
    </div>
  );
}
